import type { FC } from "react";
import { Badge, Group, Table, Text } from "@mantine/core";
import {
  IconArrowRight,
  IconCheck,
  IconChevronRight,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";

import { ProductIcon } from "@jasper/forms";
import { useProductDescription } from "@jasper/product-workflows";
import {
  getPatientLabelFirstLine,
  isRemovableItemType,
  ItemType,
} from "@jasper/shared";
import type {
  MaterialAndShadeCatalog,
  OrdersListOrder,
} from "@jasper/use-cases";
import { formatShortDate } from "/@/components/order/orderSummary";
import { OrderStatusV2 } from "/@/generated/graphql";
import { getOrderStatusV2Presentation } from "/@/pages/orders/orderStatusV2";

type OrderSummaryRowV2Props = {
  order: OrdersListOrder;
  materialAndShadeCatalog: MaterialAndShadeCatalog;
};

// On narrower screens the date goes first, then the delivery date.
export const COLUMN_VISIBLE_FROM = { date: "md", delivery: "sm" } as const;

const ATTENTION_STYLE = {
  boxShadow: "inset 3px 0 0 var(--mantine-color-amber-4)",
};

const DeliveryDate: FC<{ date: unknown; isDelivered: boolean }> = ({
  date,
  isDelivered,
}) => {
  if (!date) return "—";

  return (
    <Group gap={4} wrap="nowrap" c={isDelivered ? "success.7" : undefined}>
      {isDelivered ? (
        <IconCheck size={14} />
      ) : (
        <IconArrowRight size={14} color="var(--mantine-color-ink-3)" />
      )}
      {formatShortDate(date)}
    </Group>
  );
};

export const OrderSummaryRowV2: FC<OrderSummaryRowV2Props> = ({
  order,
  materialAndShadeCatalog,
}) => {
  const navigate = useNavigate();
  const { getOrderTitle, getOrderDetails } = useProductDescription();
  const { products } = order;
  const status = getOrderStatusV2Presentation(order.status);

  return (
    <Table.Tr
      mod={{ clickable: true }}
      onClick={() => navigate(`/order/${order.id}`)}
    >
      <Table.Td
        style={status.color === "warning" ? ATTENTION_STYLE : undefined}
      >
        <ProductIcon
          productTypes={products.map(product => product.type)}
          isRemovable={isRemovableItemType(products[0]?.type as ItemType)}
        />
      </Table.Td>
      <Table.Td>
        <Group gap="s2" wrap="nowrap">
          <Text variant="strong" truncate="end">
            {getOrderTitle(products)}
          </Text>
          {products.length > 1 && (
            <Badge variant="tag" color="violet">
              Multi
            </Badge>
          )}
        </Group>
        <Text variant="caption" truncate="end">
          {[
            order.reference,
            ...getOrderDetails(products, materialAndShadeCatalog),
          ].join(" · ")}
        </Text>
      </Table.Td>
      <Table.Td>{getPatientLabelFirstLine(order)}</Table.Td>
      <Table.Td visibleFrom={COLUMN_VISIBLE_FROM.date}>
        {formatShortDate(order.confirmedAt ?? order.createdAt)}
      </Table.Td>
      <Table.Td>
        <Badge color={status.color}>{status.label}</Badge>
      </Table.Td>
      <Table.Td visibleFrom={COLUMN_VISIBLE_FROM.delivery}>
        <DeliveryDate
          date={order.deliveryDate}
          isDelivered={order.status === OrderStatusV2.Delivered}
        />
      </Table.Td>
      <Table.Td>
        <IconChevronRight size={16} />
      </Table.Td>
    </Table.Tr>
  );
};
